import {
  BillableIcon,
  Currency,
  DeleteConfirmation,
  ExportDialog,
  SearchInput,
  ExportDropdown,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { mimeTypes } from '~/utils';
import { ActiveStatusSelect } from '../custom-data/components';
import WorkspaceRoleDrawer from './WorkspaceRoleDrawer';

function WorkspaceRolesPage() {
  const documentTitle = useDocumentTitle('Workspace Roles and Rates');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const features = useFeatures();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true', size: 1000 });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .workspaceRoles()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .workspaceRoles()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (workspaceRole) => {
    setDialog(
      <WorkspaceRoleDrawer
        workspaceRole={workspaceRole}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Workspace Roles and Rates');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  async function handleActiveStatusChange(workspaceRole, flag) {
    try {
      await api.www.workspaces(workspace.id).workspaceRoles(workspaceRole.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(workspaceRole) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Role?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www.workspaces(workspace.id).workspaceRoles(workspaceRole.id).delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Workspace Roles and Rates</Page.Title>
          <Page.Description>
            Workspace roles and rates allow you to create a company-wide rate card. These roles and rates can then be
            added or linked to client-specific rate cards.
          </Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('roles_and_rates.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('roles_and_rates.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Workspace Role" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column>Name</ListView.Column>
            <ListView.Column isVisible={features.practices}>Practice</ListView.Column>
            <ListView.Column>Location</ListView.Column>
            <ListView.Column isVisible={features.disciplines}>Discipline</ListView.Column>
            <ListView.Column align="center" width="4rem">
              Billable
            </ListView.Column>
            <ListView.Column align="right" width="10rem">
              Rate
            </ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((workspaceRole) => {
              const { id, name, discipline, practice, location, isActive, isBillable, rate, currency } = workspaceRole;

              return (
                <ListView.Row onClick={() => handleEdit(workspaceRole)} key={id} isDisabled={!isActive}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListView.Cell>{practice?.name}</ListView.Cell>
                  <ListView.Cell>{location?.name}</ListView.Cell>
                  <ListView.Cell>{discipline?.name}</ListView.Cell>
                  <ListView.Cell>
                    <BillableIcon value={isBillable} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <Currency value={isBillable ? rate : null} maximumFractionDigits={7} currency={currency} />
                  </ListView.Cell>
                  <ListViewActions>
                    <ListViewActions.Edit
                      onClick={() => {
                        handleEdit(workspaceRole);
                      }}
                    />

                    <hr />

                    <ListViewMenu>
                      {({ setIsOpen }) => (
                        <>
                          <ListViewMenu.Item
                            onClick={() => {
                              handleEdit(workspaceRole);
                              setIsOpen(false);
                            }}>
                            Edit
                          </ListViewMenu.Item>
                          <ListViewMenu.Item
                            onClick={async () => {
                              await handleActiveStatusChange(workspaceRole, !isActive);
                              setIsOpen(false);
                            }}>
                            {isActive ? 'Deactivate' : 'Activate'}
                          </ListViewMenu.Item>

                          <ListViewMenu.DeleteItem
                            tooltip="This item is currently in use."
                            onCheckDependencies={async (workspace) =>
                              (await workspace.workspaceRoles(id).hasDependencies()).data
                            }
                            onClick={() => {
                              handleDelete(workspaceRole);
                            }}>
                            Delete
                          </ListViewMenu.DeleteItem>
                        </>
                      )}
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Workspace Role" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default WorkspaceRolesPage;
